import { useState } from 'react';
import {
  Box,
  Collapse,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import EmojiSymbolsOutlinedIcon from '@mui/icons-material/EmojiSymbolsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { docs } from './docsStyles';

// Per-package section icon (matches the mockup's section glyphs).
const PACKAGE_ICONS = {
  blocks: WidgetsOutlinedIcon,
  dashboards: InsightsOutlinedIcon,
  'design-tokens': PaletteOutlinedIcon,
  'zippedi-icons': EmojiSymbolsOutlinedIcon,
  security: ShieldOutlinedIcon,
};

const packageShortName = pkg => (pkg.split('/').pop() || pkg).toUpperCase();

// Left-rail navigation (Mintlify/Tremor docs style): catalog title + search, then components
// grouped by package (uppercase section header) → category (collapsible row with a count).
function LibraryNav({ title, subtitle, groups, selectedName, onSelect, search, onSearchChange, t }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = key => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  const isEmpty = groups.every(group => group.families.length === 0);

  return (
    <Box sx={{ position: 'sticky', top: 0, fontFamily: docs.font }}>
      <Box sx={{ px: 1, pb: 1.5 }}>
        <Typography sx={{ fontFamily: docs.font, fontWeight: 700, fontSize: 15, color: docs.textStrong, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography sx={{ fontFamily: docs.font, fontSize: 11, color: docs.textFaint, mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>

      <TextField
        size="small"
        fullWidth
        value={search}
        onChange={event => onSearchChange(event.target.value)}
        placeholder={t('internalLibraries.search_placeholder', 'Search components…')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: docs.textFaint }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box
                component="span"
                sx={{
                  fontFamily: docs.font,
                  fontSize: 11,
                  color: docs.textFaint,
                  border: '1px solid',
                  borderColor: docs.border,
                  borderRadius: 1,
                  px: 0.5,
                }}>
                ⌘K
              </Box>
            </InputAdornment>
          ),
          sx: { fontFamily: docs.font, fontSize: 13, borderRadius: 2, bgcolor: docs.bgSubtle },
        }}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', pr: 0.5 }}>
        {isEmpty ? (
          <Typography sx={{ fontFamily: docs.font, fontSize: 13, color: docs.textMuted, p: 1.5 }}>
            {t('internalLibraries.no_results', 'No components match your search.')}
          </Typography>
        ) : (
          groups.map(group => {
            const PkgIcon = PACKAGE_ICONS[group.package.split('/').pop()] || FolderOutlinedIcon;
            return (
              <Box key={group.package} sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1,
                    py: 0.5,
                    color: docs.textFaint,
                  }}>
                  <PkgIcon sx={{ fontSize: 15 }} />
                  <Typography
                    sx={{
                      fontFamily: docs.font,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 0.6,
                    }}>
                    {packageShortName(group.package)}
                  </Typography>
                </Box>

                {group.families.map(family => {
                  const open = !collapsed[family.key];
                  return (
                    <Box key={family.key}>
                      <ListItemButton
                        onClick={() => toggle(family.key)}
                        sx={{ borderRadius: 2, py: 0.5, px: 1 }}>
                        <ListItemText
                          primary={family.label}
                          primaryTypographyProps={{
                            sx: { fontFamily: docs.font, fontSize: 13.5, fontWeight: 600, color: docs.text },
                          }}
                        />
                        <Box
                          component="span"
                          sx={{ fontFamily: docs.font, fontSize: 11, color: docs.textFaint, mr: 0.75 }}>
                          {family.items.length}
                        </Box>
                        {open ? (
                          <ExpandLess sx={{ fontSize: 16, color: docs.textFaint }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 16, color: docs.textFaint }} />
                        )}
                      </ListItemButton>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <List dense disablePadding sx={{ pl: 1 }}>
                          {family.items.map(component => (
                            <ListItemButton
                              key={`${component.package}/${component.name}`}
                              selected={`${component.package}/${component.name}` === selectedName}
                              onClick={() => onSelect(`${component.package}/${component.name}`)}
                              sx={{
                                borderRadius: 2,
                                py: 0.4,
                                pl: 2.5,
                                '&.Mui-selected': {
                                  bgcolor: docs.selectedBg,
                                  '&:hover': { bgcolor: docs.selectedBg },
                                },
                              }}>
                              <ListItemText
                                primary={component.name}
                                primaryTypographyProps={{
                                  sx: {
                                    fontFamily: docs.font,
                                    fontSize: 13,
                                    color:
                                      component.name === selectedName ? docs.selectedText : docs.textMuted,
                                    fontWeight: component.name === selectedName ? 600 : 400,
                                  },
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default LibraryNav;
